import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'gold' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const variants = {
      primary: 'bg-wine text-white hover:bg-wine-light active:bg-wine-dark disabled:bg-neutral-300',
      secondary: 'bg-transparent text-wine border border-wine hover:bg-wine/10 disabled:border-neutral-300 disabled:text-neutral-400',
      ghost: 'bg-transparent text-wine hover:bg-neutral-100 disabled:text-neutral-400',
      gold: 'bg-gold text-white hover:bg-gold-light active:bg-gold-dark disabled:bg-neutral-300',
      danger: 'bg-error text-white hover:bg-red-700 active:bg-red-800 disabled:bg-neutral-300',
    };

    const sizes = {
      sm: 'px-4 py-2 text-sm min-h-[36px] gap-1.5',
      md: 'px-6 py-3 text-base min-h-[44px] gap-2',
      lg: 'px-8 py-4 text-lg min-h-[52px] gap-2.5',
    };

    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center font-medium rounded-lg transition-colors duration-200 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-wine focus:ring-offset-2',
          variants[variant],
          sizes[size],
          className
        )}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : leftIcon ? (
          <span className="shrink-0">{leftIcon}</span>
        ) : null}
        {children}
        {rightIcon && !isLoading && <span className="shrink-0">{rightIcon}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button };
